import {
  BulbOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  CodeOutlined,
  SaveOutlined,
  WarningOutlined
} from "@ant-design/icons";
import {
  Button,
  Empty,
  Modal,
  Progress,
  Space,
  Tag,
  Typography,
  Watermark,
} from "antd";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ScreeningExportPDFButton } from "./ScreeningPDFTemplate";
import "./ScreeningReport.scss";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CandidateInfo {
  name?: string;
  role?: string;
  experience_years?: number;
  primary_skills?: string[];
}

export interface FitOverview {
  fit_score?: number;
  fit_category?: string;
  confidence?: string;
  recommendation?: string;
  summary?: string;
}

export interface JobRequirementCoverageItem {
  requirement_id?: string;
  requirement?: string;
  priority?: string;
  evidence?: string;
  coverage?: string;
}

export interface SkillItem {
  name: string;
  description?: string;
}

export interface SkillMatchBreakdown {
  strong?: (string | SkillItem)[];
  partial?: (string | SkillItem)[];
  missing?: (string | SkillItem)[];
}

export interface ValidationGap {
  skill?: string;
  reason?: string;
}

export interface RecommendedAssessment {
  skill_area?: string;
  reason_to_validate?: string;
  assessment_type?: string;
}

export interface StructuredScreeningReport {
  candidate?: CandidateInfo;
  fit_overview?: FitOverview;
  job_requirement_coverage?: JobRequirementCoverageItem[];
  skill_match_breakdown?: SkillMatchBreakdown;
  validation_gaps?: ValidationGap[];
  recommended_assessments?: RecommendedAssessment[];
}

export interface LegacyHtmlReport {
  html?: string;
}

type ReportPayload = StructuredScreeningReport | LegacyHtmlReport | null | undefined;

function isLegacyHtmlReport(report: ReportPayload): report is LegacyHtmlReport {
  return !!report && typeof (report as any).html === "string";
}

export interface ScreeningReportProps {
  sessionId: string;
  report: ReportPayload;
  showWatermark?: boolean;   // default: true
  showPublicCTAs?: boolean;  // default: true — shows footer bar + protection hooks
  onCtaClick?: () => void;   // deprecated, kept for backward compat
  onSaveCtaClick?: () => void;    // "Save and Upload Multiple Resumes" CTA (cta_type=save)
  onValidateCtaClick?: () => void; // "Validate All Missing Skills" CTA (cta_type=validate)
  onHandleTryAnotherClick?: () => void; // "Try Profile Matching for Another Candidate" CTA
}

// ─── Utility helpers ─────────────────────────────────────────────────────────

function normalizeSkills(items?: (string | SkillItem)[]): SkillItem[] {
  return (items ?? []).map((i) =>
    typeof i === "string" ? { name: i, description: undefined } : i
  );
}

function getFitScoreColor(score: number): string {
  if (score >= 70) return "#52c41a";
  if (score >= 40) return "#fa8c16";
  return "#ff4d4f";
}

function getCoverageBarConfig(coverage?: string): {
  color: string;
  filled: number;
  total: number;
  label: string;
  labelColor: string;
} {
  const c = coverage?.toLowerCase() ?? "";
  if (c === "strong") return { color: "#52c41a", filled: 4, total: 4, label: "Strong", labelColor: "#52c41a" };
  if (c === "partial") return { color: "#fa8c16", filled: 2, total: 4, label: "Partial", labelColor: "#fa8c16" };
  if (c === "medium") return { color: "#fa8c16", filled: 2, total: 4, label: "Medium", labelColor: "#fa8c16" };
  if (c === "weak") return { color: "#ff4d4f", filled: 1, total: 4, label: "Weak", labelColor: "#ff4d4f" };
  return { color: "#ff4d4f", filled: 0, total: 4, label: "Missing", labelColor: "#ff4d4f" };
}

function getPriorityTagColor(priority?: string): string {
  const p = priority?.toLowerCase() ?? "";
  if (p.includes("must")) return "red";
  if (p.includes("nice")) return "blue";
  return "default";
}

function formatDate(date: Date): string {
  return date
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    .toUpperCase();
}

// ─── Protection hooks ─────────────────────────────────────────────────────────
// NOTE: All protections below are SOFT/CLIENT-SIDE only.
// A determined user can bypass them via browser DevTools or by disabling JS.
// They serve as a UX deterrent, not a security guarantee.

function useReportProtection(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    const onContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener("contextmenu", onContextMenu);

    const onKeyDown = (e: KeyboardEvent) => {
      const { key, ctrlKey, shiftKey, metaKey } = e;
      if (
        key === "F12" ||
        (ctrlKey && shiftKey && ["I", "J", "C"].includes(key)) ||
        (ctrlKey && key.toLowerCase() === "u") ||
        (metaKey && shiftKey && ["I", "J", "C"].includes(key)) ||
        (metaKey && key.toLowerCase() === "u")
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled]);
}



// ─── Sub-components ───────────────────────────────────────────────────────────

const ReportHeader: React.FC<{ candidate?: CandidateInfo }> = ({ candidate }) => {
  const generatedDate = formatDate(new Date());
  const expYears =
    candidate?.experience_years != null
      ? Number(candidate.experience_years).toFixed(1)
      : null;

  return (
    <div className="sr-header">
      <div className="sr-header__top-row">
        <div className="sr-header__left">
          <Typography.Title level={5} className="sr-header__name">{candidate?.name ?? "Candidate"}</Typography.Title>
          {candidate?.role && (
            <Tag style={{ marginLeft: 4, fontSize: 12, padding: "2px 10px", borderRadius: 20, marginTop: 4, maxWidth: "100px", textWrap: "wrap" }}>
              {candidate.role}
            </Tag>
          )}
        </div>
        <span className="sr-header__generated">Generated: {generatedDate}</span>
      </div>

      <div className="sr-header__meta-row">
        {expYears && (
          <span className="sr-header__meta-item">
            <ClockCircleOutlined />
            Exp: {expYears} Years
          </span>
        )}
        {candidate?.primary_skills && candidate.primary_skills.length > 0 && (
          <span className="sr-header__meta-item">
            <CodeOutlined />
            Primary Skills: {candidate.primary_skills.join(", ")}
          </span>
        )}
      </div>
    </div>
  );
};

const FitOverviewSection: React.FC<{ fit: FitOverview }> = ({ fit }) => {
  const score = typeof fit.fit_score === "number" ? fit.fit_score : undefined;
  const color = score != null ? getFitScoreColor(score) : "#aaa";
  const tagColor = score != null && score >= 70 ? "green" : score != null && score >= 40 ? "orange" : "red";

  return (
    <div className="sr-section">
      <div className="sr-fit">
        <div className="sr-fit__left">
          {score != null ? (
            <Progress
              type="circle"
              percent={Math.max(0, Math.min(100, score))}
              width={130}
              strokeColor={color}
              format={(p) => (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{p}</div>
                  <div style={{ fontSize: 10, color: "#aaa", letterSpacing: 1, marginTop: 4 }}>FIT SCORE</div>
                </div>
              )}
            />
          ) : (
            <div style={{ width: 130, height: 130, borderRadius: "50%", background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#aaa", fontSize: 12 }}>N/A</span>
            </div>
          )}

          {fit.fit_category && (
            <div className="sr-fit__category">
              <Tag color={tagColor} style={{ borderRadius: 20, fontSize: 11, padding: "2px 10px", maxWidth: '100px', textWrap: 'wrap' }}>
                {fit.fit_category}
              </Tag>
            </div>
          )}

          {fit.confidence && (
            <span className="sr-fit__confidence">Confidence: {fit.confidence}</span>
          )}
        </div>

        <div className="sr-fit__right">
          {fit.recommendation && (
            <div className="sr-fit__box">
              <div className="sr-fit__box-label sr-fit__box-label--recommendation">
                <CheckCircleOutlined />
                Recommendation
              </div>
              <Typography.Paragraph className="sr-fit__box-text">{fit.recommendation}</Typography.Paragraph>
            </div>
          )}
          {fit.summary && (
            <div className="sr-fit__box">
              <div className="sr-fit__box-label sr-fit__box-label--summary">
                <BulbOutlined />
                Summary Insight
              </div>
              <Typography.Paragraph className="sr-fit__box-text">{fit.summary}</Typography.Paragraph>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AssessmentFocusSection: React.FC<{
  fitScore: number | 0;
  assessments: RecommendedAssessment[];
  onCTA: () => void;
  sessionId?: string;
}> = ({ fitScore, assessments, onCTA, sessionId }) => {
  const handleAssessmentCTA = () => {
    onCTA();
  };

  return (
    <div className="sr-section sr-section--assess">
      {fitScore >= 45 ? (
        <>
          <div className="sr-assess__header">
            <Space direction="vertical" size={4}>
              <Typography.Title level={5} className="sr-assess__title">QualifyMe Recommended Assessments</Typography.Title>
              <Typography.Text type="secondary" className="sr-assess__subtitle">Assess the following skills of candidate before considering for this role</Typography.Text>
            </Space>
            <Tag
              color="#001529"
              style={{
                borderRadius: 20,
                fontSize: 11,
                padding: "3px 12px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              ★ Recommended
            </Tag>
          </div>

          <div className="sr-assess__col-headers">
            <span className="sr-assess__col-skill">Skill Area</span>
            <span className="sr-assess__col-detail">Why Validate &amp; Suggested Assessment</span>
          </div>

          {assessments.map((a, i) => (
            <div key={i} className="sr-assess__row">
              <div className="sr-assess__row-skill">{a.skill_area ?? "—"}</div>
              <div className="sr-assess__row-detail">
                <div className="sr-assess__row-reason">
                  <WarningOutlined />
                  <span>{a.reason_to_validate ?? "—"}</span>
                </div>
                {/* {a.assessment_type && (
              <div className="sr-assess__row-suggested">Suggested: {a.assessment_type}</div>
            )} */}
              </div>
            </div>
          ))}
        </>
      ) : (
        <Typography.Title type="secondary" level={5} >
          This candidate is not recommended as key skills are missing. However, if you feel that the candidates resume does not reflect the candidates capabilities, try sending QualifyMe assessment to the candidate and get a detailed report
        </Typography.Title>
      )}

      <div className="sr-assess__cta sr-pdf-hide">
        <Typography.Text type="secondary" className="sr-assess__cta-note">
          QualifyMe's AI driven assessments can automatically assess the candidate for these skills and give you a final report
        </Typography.Text>
        <Button type="primary" size="large" className="sr-assess__cta-btn button-shine" onClick={handleAssessmentCTA}>
          <img
            src="/images/qualifyme-logo-dark.png"
            alt="QualifyMe Logo"
            style={{ width: '44px', height: "44px", marginRight: 16 }}
          />Send QualifyMe Assessment Now
        </Button>
      </div>
    </div>
  );
};

const JobCoverageSection: React.FC<{ coverage: JobRequirementCoverageItem[] }> = ({ coverage }) => {

  // normalize values safely
  const normalize = (val?: string) =>
    val?.toLowerCase().replace(/\s/g, "") || "";

  const coverageRank = (coverage?: string) => {
    const c = normalize(coverage);

    if (c === "strong") return 1;
    if (c === "medium" || c === "partial") return 2;
    if (c === "weak") return 3;
    if (c === "missing") return 4;

    return 5; // unknown last
  };

  // detect Must-have + weak/missing
  const isCriticalGap = (item: JobRequirementCoverageItem) => {
    const priority = normalize(item.priority);
    const cov = normalize(item.coverage);

    return (
      priority.includes("must") &&
      (cov === "missing" || cov === "weak")
    );
  };

  // group items
const mustHave = coverage
  .filter(i => normalize(i.priority).includes("must"))
  .sort((a, b) => coverageRank(a.coverage) - coverageRank(b.coverage));

const niceToHave = coverage
  .filter(i => !normalize(i.priority).includes("must"))
  .sort((a, b) => coverageRank(a.coverage) - coverageRank(b.coverage));

  // reusable row renderer
  const renderRow = (item: JobRequirementCoverageItem, i: number) => {
    const bar = getCoverageBarConfig(item.coverage);
    const highlight = isCriticalGap(item);

    return (
      <div
        key={item.requirement_id ?? i}
        className={`sr-coverage__row ${highlight ? "sr-coverage__row--critical" : ""
          }`}
      >
        <div className="sr-coverage__row-req">
          {item.requirement ?? "—"}
        </div>

        <div className="sr-coverage__row-priority">
          <Tag
            color={getPriorityTagColor(item.priority)}
            style={{
              borderRadius: 4,
              fontSize: 10,
              letterSpacing: 0.5,
              fontWeight: 600,
              textTransform: "uppercase"

            }}
          >
            {item.priority ?? "—"}
          </Tag>
        </div>

        <div className="sr-coverage__row-proficiency">
          <div className="sr-coverage__bar">
            {Array.from({ length: bar.total }).map((_, idx) => (
              <div
                key={idx}
                className="sr-coverage__bar-segment"
                style={{
                  background: idx < bar.filled ? bar.color : "#e8e8e8"
                }}
              />
            ))}
          </div>

          <span
            className="sr-coverage__proficiency-label"
            style={{ color: bar.labelColor }}
          >
            {bar.label}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="sr-section">
      <Typography.Title
        level={5}
        className="sr-assess__title"
        style={{ marginBottom: 20, borderLeftColor: "#1a1a2e" }}
      >
        Job Requirement Coverage Overview
      </Typography.Title>

      <div className="sr-coverage__col-headers">
        <span className="sr-coverage__col-req">Job Requirement</span>
        <span className="sr-coverage__col-priority">Priority</span>
        <span className="sr-coverage__col-proficiency">Proficiency Match</span>
      </div>

      {/* MUST HAVE */}
      {mustHave.length > 0 && (
        <>
          <div className="sr-coverage__group-title" style={{ marginTop: 8 }}>Must Have</div>
          {mustHave.map(renderRow)}
        </>
      )}

      {/* NICE TO HAVE */}
      {niceToHave.length > 0 && (
        <>
          <div className="sr-coverage__group-title" style={{ marginTop: 8 }}>Nice to Have</div>
          {niceToHave.map(renderRow)}
        </>
      )}
    </div>
  );
};

const SkillBreakdownSection: React.FC<{
  skills: SkillMatchBreakdown;
}> = ({ skills }) => {
  const strongItems = normalizeSkills(skills.strong);
  const partialItems = normalizeSkills(skills.partial);
  const missingItems = normalizeSkills(skills.missing);

  return (
    <>
      <div className="sr-skills-grid">
        <div className="sr-skills__column">
          <div className="sr-skills__col-header">
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
            <span className="sr-skills__col-title sr-skills__col-title--strong">Strongly Matched</span>
          </div>
          {strongItems.length === 0
            ? <span style={{ color: "#aaa", fontSize: 12 }}>None identified</span>
            : strongItems.map((s, i) => (
              <div key={i} className="sr-skills__item">
                <div className="sr-skills__item-name">{s.name}</div>
                {s.description && <div className="sr-skills__item-desc">{s.description}</div>}
              </div>
            ))}
        </div>

        <div className="sr-skills__column">
          <div className="sr-skills__col-header">
            <CheckCircleOutlined style={{ color: "#fa8c16", fontSize: 16 }} />
            <span className="sr-skills__col-title sr-skills__col-title--partial">Partially Matched</span>
          </div>
          {partialItems.length === 0
            ? <span style={{ color: "#aaa", fontSize: 12 }}>None identified</span>
            : partialItems.map((s, i) => (
              <div key={i} className="sr-skills__item">
                <div className="sr-skills__item-name">{s.name}</div>
                {s.description && <div className="sr-skills__item-desc">{s.description}</div>}
              </div>
            ))}
        </div>

        <div className="sr-skills__column">
          <div className="sr-skills__col-header">
            <CheckCircleOutlined style={{ color: "#aaa", fontSize: 16 }} />
            <span className="sr-skills__col-title sr-skills__col-title--missing">Missing / Unclear</span>
          </div>
          {missingItems.length === 0
            ? <span style={{ color: "#aaa", fontSize: 12 }}>None identified</span>
            : missingItems.map((s, i) => (
              <div key={i} className="sr-skills__item">
                <div className="sr-skills__item-name">{s.name}</div>
                {s.description && <div className="sr-skills__item-desc">{s.description}</div>}
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

const ValidationGapsSection: React.FC<{
  gaps: ValidationGap[];
}> = ({ gaps }) => (
  <div className="sr-section sr-gaps">
    <div className="sr-gaps__title-row">
      <WarningOutlined />
      <div className="sr-gaps__title">Validation Gaps (Pre-Assessment Risk)</div>
    </div>

    <div className="sr-gaps__insight">
      <strong>Insight:</strong> Resume-based analysis provides directional insights. These gaps introduce moderate hiring risk if not validated before interviews.
    </div>

    <p className="sr-gaps__body">
      Based on resume analysis alone, the following cannot be reliably assessed:
    </p>

    <ul className="sr-gaps__list">
      {gaps.map((gap, i) => (
        <li key={i}>
          {gap.skill && <strong>{gap.skill}</strong>}
          {gap.skill && gap.reason && " — "}
          {gap.reason}
        </li>
      ))}
    </ul>
  </div>
);

const ReportFooterBar: React.FC<{
  onSave: () => void;
  exportButton: React.ReactNode;
  sessionId?: string;
}> = ({ onSave, exportButton, sessionId }) => {
  const handleUploadResumesClick = () => {
    onSave();
  };

  return (
    <div className="sr-footer-bar">
      <div className="sr-footer-bar__inner">
        <div className="sr-footer-bar__actions">
          <Button className=" button-shine" size="large" type="primary" icon={<CloudUploadOutlined />} onClick={handleUploadResumesClick}>
            Upload Multiple Resumes
          </Button>
          {exportButton}
        </div>
      </div>
    </div>
  );
};


// ─── Main Component ───────────────────────────────────────────────────────────

const ScreeningReport: React.FC<ScreeningReportProps> = ({
  sessionId,
  report,
  showWatermark = true,
  showPublicCTAs = true,
  onCtaClick,
  onSaveCtaClick,
  onValidateCtaClick,
  onHandleTryAnotherClick
}) => {
  const navigate = useNavigate();
  useReportProtection(showPublicCTAs);

  // Default sign-up handler (no cta_type, used as fallback)
  const defaultSignUp = () => {
    if (onCtaClick) {
      onCtaClick();
      return;
    }
    navigate(`/sign-up?screening_session_id=${encodeURIComponent(sessionId)}`);
  };

  const handleSignUp = defaultSignUp;
  const handleSaveSignUp = onSaveCtaClick ?? defaultSignUp;
  const handleValidateSignUp = onValidateCtaClick ?? defaultSignUp;


  if (!report) {
    return (
      <div className="sr-container">
        <div className="sr-section" style={{ textAlign: "center", color: "#aaa", padding: 48 }}>
          <Empty description="No report available yet." />
        </div>
      </div>
    );
  }

  if (isLegacyHtmlReport(report)) {
    return (
      <div className="sr-container">
        <div className="sr-section">
          <p style={{ color: "#888" }}>Legacy HTML report — please contact support.</p>
        </div>
      </div>
    );
  }

  const structured = report as StructuredScreeningReport;
  const candidate = structured.candidate ?? {};
  const fit = structured.fit_overview;
  const skills = structured.skill_match_breakdown ?? {};
  const coverageList = structured.job_requirement_coverage ?? [];
  const validationGaps = structured.validation_gaps ?? [];
  const recommended = structured.recommended_assessments ?? [];

  const hasSkills =
    (skills.strong?.length ?? 0) > 0 ||
    (skills.partial?.length ?? 0) > 0 ||
    (skills.missing?.length ?? 0) > 0;

  const reportContent = (
    <div className="sr-container" id="screening-report-root">
      <ReportHeader candidate={candidate} />

      {fit && <FitOverviewSection fit={fit} />}

      {recommended.length > 0 && fit && (
        <AssessmentFocusSection fitScore={fit?.fit_score || 0} assessments={recommended} onCTA={handleValidateSignUp} sessionId={sessionId} />
      )}

      {coverageList.length > 0 && (
        <JobCoverageSection coverage={coverageList} />
      )}

      {hasSkills && (<SkillBreakdownSection skills={skills} />)}

      {validationGaps.length > 0 && (
        <ValidationGapsSection gaps={validationGaps} />
      )}

      <div className="sr-try-another">
        <Button size="large" onClick={onHandleTryAnotherClick}>
          ← Try Profile Matching for Another Candidate
        </Button>
      </div>

      {showPublicCTAs && (
        <ReportFooterBar
          onSave={handleSaveSignUp}
          exportButton={
            <ScreeningExportPDFButton
              sessionId={sessionId}
            />
          }
          sessionId={sessionId}
        />
      )}

    </div>
  );

  if (!showWatermark) return reportContent;

  return (
    <Watermark
      content="QualifyMe"
      gap={[100, 100]}
      font={{ color: "rgba(0,0,0,0.06)", fontSize: 16 }}
      rotate={-22}
    >
      {reportContent}
    </Watermark>
  );
};

export default ScreeningReport;
